/* eslint-disable max-lines -- Pre-existing violation */
import {
  Button,
  Card,
  DataStatus,
  Form,
  FormButton,
  FormField,
  openDeleteDialog,
  showNotification,
} from 'platform/components';
import {Grid, HStack, Right, Show, Space, Text, VStack} from 'platform/foundation';
import {z} from 'zod';

import {useMemo, useState} from 'react';

import {
  PromptBridgeCredentials,
  PromptBridgePlatformResponseBody,
  FastbackDealer,
  useDeletePromptBridgePlatformBranchCredentialsMutation,
  useGetPromptBridgePlatformQuery,
  useValidateFastbackDealersMutation,
  usePromptBridgePlatformAuthenticateMutation,
} from '@dms/api/sales';
import {AdvertisingPlatformCodeEnum} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {useBranchId} from '@dms/settings/shared';
import {handleApiError} from '@dms/shared';

type PromptBridgeCredentialsFormData = Record<string, unknown>;

type CredentialFieldConfig = {
  name: string;
  label: string;
  type: 'text' | 'password' | 'number' | 'checkbox' | 'choice' | 'multiChoice' | 'textarea';
  required?: boolean;
  secret?: boolean;
  options?: Array<{label: string; value: string}>;
  defaultValue?: unknown;
  helperText?: string;
  parse?: (value: unknown) => unknown;
  format?: (value: unknown) => unknown;
};

type PromptBridgePlatformConfig = {
  code: AdvertisingPlatformCodeEnum;
  fields: CredentialFieldConfig[];
};

const LOCALE_OPTIONS = ['nl', 'fr', 'en', 'de'].map((code) => ({label: code, value: code}));
const TWO_DEHANDS_LOCALE_OPTIONS = ['nl-BE', 'fr-BE'].map((code) => ({label: code, value: code}));
const TOYOTA_NMSC_OPTIONS = ['TBEL', 'THEL', 'TRO', 'TBGR', 'LEX'].map((code) => ({
  label: code,
  value: code,
}));
const TOYOTA_MARKET_OPTIONS = ['BE', 'GR', 'RO', 'BG', 'LU'].map((code) => ({
  label: code,
  value: code,
}));
const SPOTICAR_BRAND_OPTIONS = ['peugeot', 'citroen', 'ds', 'opel', 'vauxhall'].map((code) => ({
  label: code,
  value: code,
}));
const SPOTICAR_MARKET_OPTIONS = ['BE', 'LU'].map((code) => ({label: code, value: code}));

const textList = {
  parse: (value: unknown) =>
    String(value ?? '')
      .split(/[\n,]/)
      .map((item) => item.trim())
      .filter(Boolean),
  format: (value: unknown) => (Array.isArray(value) ? value.join('\n') : ''),
};

const concessionList = {
  parse: (value: unknown) =>
    String(value ?? '')
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const [
          concessionId = '',
          name = '',
          email = '',
          phone = '',
          address = '',
          postcode = '',
          vat = '',
          carflowName = '',
        ] = line.split(';').map((part) => part.trim());

        return {
          concessionId,
          name,
          email,
          phone,
          address,
          postcode,
          vat,
          carflowName: carflowName || undefined,
        };
      }),
  format: (value: unknown) =>
    Array.isArray(value)
      ? value
          .map((item) => {
            const concession = item as Record<string, unknown>;
            return [
              concession.concessionId,
              concession.name,
              concession.email,
              concession.phone,
              concession.address,
              concession.postcode,
              concession.vat,
              concession.carflowName,
            ]
              .filter((part) => part !== undefined && part !== null)
              .join('; ');
          })
          .join('\n')
      : '',
};

const mergeGroupList = {
  parse: (value: unknown) =>
    String(value ?? '')
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const [concessionIds = '', targetFilename = ''] = line
          .split(':')
          .map((part) => part.trim());

        return {
          concessionIds: concessionIds
            .split(',')
            .map((item) => item.trim())
            .filter(Boolean),
          targetFilename,
        };
      }),
  format: (value: unknown) =>
    Array.isArray(value)
      ? value
          .map((item) => {
            const group = item as {concessionIds?: unknown; targetFilename?: unknown};
            const concessionIds = Array.isArray(group.concessionIds)
              ? group.concessionIds.join(',')
              : '';

            return `${concessionIds}: ${group.targetFilename ?? ''}`;
          })
          .join('\n')
      : '',
};

const promptBridgePlatformConfigs: Partial<
  Record<AdvertisingPlatformCodeEnum, PromptBridgePlatformConfig>
> = {
  [AdvertisingPlatformCodeEnum.AUTOMARKETPL]: {
    code: AdvertisingPlatformCodeEnum.AUTOMARKETPL,
    fields: [
      {name: 'apiKey', label: 'API key', type: 'password', required: true, secret: true},
      {
        name: 'credentialKey',
        label: 'Credential key',
        type: 'password',
        required: true,
        secret: true,
      },
    ],
  },
  [AdvertisingPlatformCodeEnum.AUTOSCOUT24]: {
    code: AdvertisingPlatformCodeEnum.AUTOSCOUT24,
    fields: [
      {name: 'username', label: 'Username', type: 'text', required: true},
      {name: 'password', label: 'Password', type: 'password', required: true, secret: true},
      {name: 'customerId', label: 'Customer ID', type: 'text', required: true},
      {name: 'testMode', label: 'Test mode', type: 'checkbox', defaultValue: false},
    ],
  },
  [AdvertisingPlatformCodeEnum.MOBILE_DE]: {
    code: AdvertisingPlatformCodeEnum.MOBILE_DE,
    fields: [
      {name: 'username', label: 'Username', type: 'text', required: true},
      {name: 'password', label: 'Password', type: 'password', required: true, secret: true},
      {name: 'sellerId', label: 'Seller ID', type: 'text', required: true},
    ],
  },
  [AdvertisingPlatformCodeEnum.OTOMOTO]: {
    code: AdvertisingPlatformCodeEnum.OTOMOTO,
    fields: [
      {name: 'username', label: 'Username', type: 'text', required: true},
      {name: 'password', label: 'Password', type: 'password', required: true, secret: true},
    ],
  },
  [AdvertisingPlatformCodeEnum.AUTOPLAC_PL]: {
    code: AdvertisingPlatformCodeEnum.AUTOPLAC_PL,
    fields: [
      {name: 'username', label: 'Username', type: 'text', required: true},
      {name: 'password', label: 'Password', type: 'password', required: true, secret: true},
    ],
  },
  [AdvertisingPlatformCodeEnum.AUTOCARIS]: {
    code: AdvertisingPlatformCodeEnum.AUTOCARIS,
    fields: [
      {name: 'username', label: 'Username', type: 'text', required: true},
      {name: 'password', label: 'Password', type: 'password', required: true, secret: true},
    ],
  },
  [AdvertisingPlatformCodeEnum.HYUNDAI_PROMISE]: {
    code: AdvertisingPlatformCodeEnum.HYUNDAI_PROMISE,
    fields: [
      {name: 'username', label: 'Username', type: 'text', required: true},
      {name: 'password', label: 'Password', type: 'password', required: true, secret: true},
      {name: 'dealerId', label: 'Dealer ID', type: 'text', required: true},
    ],
  },
  [AdvertisingPlatformCodeEnum.MERCEDES_NA_SKLADE_REST]: {
    code: AdvertisingPlatformCodeEnum.MERCEDES_NA_SKLADE_REST,
    fields: [{name: 'dealerId', label: 'Dealer ID', type: 'text', required: true}],
  },
  [AdvertisingPlatformCodeEnum.HEXON]: {
    code: AdvertisingPlatformCodeEnum.HEXON,
    fields: [
      {name: 'advertiserId', label: 'Advertiser ID', type: 'text', required: true},
      {name: 'dealerIdInDv', label: 'DV dealer ID', type: 'text', required: true},
      {
        name: 'xmlEndpoint',
        label: 'XML endpoint',
        type: 'text',
        required: true,
        defaultValue: 'https://import.doorlinkenvoorraad.nl/fastback',
      },
      {name: 'xmlUsername', label: 'XML username', type: 'text', required: true},
      {name: 'xmlPassword', label: 'XML password', type: 'password', required: true, secret: true},
      {
        name: 'enabledSites',
        label: 'Enabled sites',
        type: 'textarea',
        required: true,
        helperText: 'One value per line',
        ...textList,
      },
      {
        name: 'supportedLocales',
        label: 'Supported locales',
        type: 'multiChoice',
        options: LOCALE_OPTIONS,
        defaultValue: ['nl'],
      },
      {
        name: 'postMode',
        label: 'Post mode',
        type: 'choice',
        options: [
          {label: 'XML request', value: 'xmlRequest'},
          {label: 'Direct body', value: 'direct-body'},
        ],
        defaultValue: 'xmlRequest',
      },
    ],
  },
  [AdvertisingPlatformCodeEnum.TOYOTA_UCL]: {
    code: AdvertisingPlatformCodeEnum.TOYOTA_UCL,
    fields: [
      {name: 'baseUrl', label: 'Base URL', type: 'text', required: true},
      {name: 'nmsc', label: 'NMSC', type: 'choice', options: TOYOTA_NMSC_OPTIONS, required: true},
      {name: 'clientId', label: 'Client ID', type: 'text', required: true},
      {
        name: 'clientSecret',
        label: 'Client secret',
        type: 'password',
        required: true,
        secret: true,
      },
      {name: 'retailerId', label: 'Retailer ID', type: 'text', required: true},
      {
        name: 'market',
        label: 'Market',
        type: 'choice',
        options: TOYOTA_MARKET_OPTIONS,
        required: true,
      },
      {name: 'tokenTtlSeconds', label: 'Token TTL seconds', type: 'number'},
    ],
  },
  [AdvertisingPlatformCodeEnum.TWO_DEHANDS]: {
    code: AdvertisingPlatformCodeEnum.TWO_DEHANDS,
    fields: [
      {name: 'oauthClientId', label: 'OAuth client ID', type: 'text', required: true},
      {
        name: 'oauthClientSecret',
        label: 'OAuth client secret',
        type: 'password',
        required: true,
        secret: true,
      },
      {
        name: 'authorizeUrl',
        label: 'Authorize URL',
        type: 'text',
        required: true,
        defaultValue: 'https://auth.2dehands.be/accounts/oauth/authorize',
      },
      {
        name: 'tokenUrl',
        label: 'Token URL',
        type: 'text',
        required: true,
        defaultValue: 'https://auth.2dehands.be/accounts/oauth/token',
      },
      {
        name: 'apiBaseUrl',
        label: 'API base URL',
        type: 'text',
        required: true,
        defaultValue: 'https://api.2dehands.be',
      },
      {
        name: 'locale',
        label: 'Locale',
        type: 'choice',
        options: TWO_DEHANDS_LOCALE_OPTIONS,
        defaultValue: 'nl-BE',
      },
      {name: 'dealerCode', label: 'Dealer code', type: 'text', required: true},
      {name: 'dealerEmail', label: 'Dealer email', type: 'text', required: true},
      {name: 'descBillingue', label: 'Bilingual description', type: 'checkbox', defaultValue: true},
      {
        name: 'premiumLabels',
        label: 'Premium labels',
        type: 'textarea',
        helperText: 'One value per line',
        ...textList,
      },
      {name: 'accessToken', label: 'Access token', type: 'password', secret: true},
      {name: 'refreshToken', label: 'Refresh token', type: 'password', secret: true},
    ],
  },
  [AdvertisingPlatformCodeEnum.LUXAUTO]: {
    code: AdvertisingPlatformCodeEnum.LUXAUTO,
    fields: [
      {name: 'luxautoId', label: 'Luxauto ID', type: 'text', required: true},
      {name: 'luxautoIds', label: 'Luxauto IDs', type: 'text'},
      {name: 'dealerSlug', label: 'Dealer slug', type: 'text'},
      {
        name: 'sftpHost',
        label: 'SFTP host',
        type: 'text',
        required: true,
        defaultValue: 'mqfa.ftp.infomaniak.com',
      },
      {name: 'sftpPort', label: 'SFTP port', type: 'number', required: true, defaultValue: 22},
      {name: 'sftpUser', label: 'SFTP user', type: 'text', required: true},
      {
        name: 'sftpPassword',
        label: 'SFTP password',
        type: 'password',
        required: true,
        secret: true,
      },
      {
        name: 'remotePath',
        label: 'Remote path',
        type: 'text',
        required: true,
        defaultValue: './web/luxauto',
      },
      {name: 'timeoutMs', label: 'Timeout ms', type: 'number', defaultValue: 30000},
    ],
  },
  [AdvertisingPlatformCodeEnum.MYCAR_LU]: {
    code: AdvertisingPlatformCodeEnum.MYCAR_LU,
    fields: [
      {name: 'dealerCode', label: 'Dealer code', type: 'text', required: true},
      {name: 'sftpHost', label: 'SFTP host', type: 'text', required: true},
      {name: 'sftpPort', label: 'SFTP port', type: 'number', required: true, defaultValue: 22},
      {name: 'sftpUser', label: 'SFTP user', type: 'text', required: true},
      {
        name: 'sftpPassword',
        label: 'SFTP password',
        type: 'password',
        required: true,
        secret: true,
      },
      {
        name: 'remotePath',
        label: 'Remote path',
        type: 'text',
        required: true,
        defaultValue: 'export_mycar.xml',
      },
      {name: 'isAutopolis', label: 'Autopolis', type: 'checkbox', defaultValue: false},
      {name: 'timeoutMs', label: 'Timeout ms', type: 'number'},
    ],
  },
  [AdvertisingPlatformCodeEnum.VROOM]: {
    code: AdvertisingPlatformCodeEnum.VROOM,
    fields: [
      {
        name: 'baseUrl',
        label: 'Base URL',
        type: 'text',
        required: true,
        defaultValue: 'https://portal.vroom.be',
      },
      {name: 'basicUsername', label: 'Basic username', type: 'text', required: true},
      {
        name: 'basicPassword',
        label: 'Basic password',
        type: 'password',
        required: true,
        secret: true,
      },
      {name: 'vroomCompanyId', label: 'Vroom company ID', type: 'text', required: true},
      {name: 'carpassIdFallback', label: 'Car-Pass ID fallback', type: 'text'},
    ],
  },
  [AdvertisingPlatformCodeEnum.CARFLOW]: {
    code: AdvertisingPlatformCodeEnum.CARFLOW,
    fields: [
      {name: 'dealerCode', label: 'Dealer code', type: 'text', required: true},
      {
        name: 'ftpHost',
        label: 'FTP host',
        type: 'text',
        required: true,
        defaultValue: 'ftp.carflowmanager.be',
      },
      {name: 'ftpPort', label: 'FTP port', type: 'number', required: true, defaultValue: 21},
      {name: 'ftpUser', label: 'FTP user', type: 'text', required: true},
      {name: 'ftpPassword', label: 'FTP password', type: 'password', required: true, secret: true},
      {name: 'passive', label: 'Passive FTP', type: 'checkbox', defaultValue: true},
      {name: 'remotePath', label: 'Remote path', type: 'text', required: true, defaultValue: '.'},
      {name: 'timeoutMs', label: 'Timeout ms', type: 'number', defaultValue: 30000},
      {
        name: 'concessions',
        label: 'Concessions',
        type: 'textarea',
        required: true,
        helperText: 'One concession per line: ID; name; email; phone; address; postcode; VAT',
        ...concessionList,
      },
      {
        name: 'mergeGroups',
        label: 'Merge groups',
        type: 'textarea',
        helperText: 'One merge group per line: concession IDs separated by comma: target filename',
        ...mergeGroupList,
      },
    ],
  },
  [AdvertisingPlatformCodeEnum.TCS]: {
    code: AdvertisingPlatformCodeEnum.TCS,
    fields: [
      {name: 'carPassId', label: 'Car-Pass ID', type: 'text', required: true},
      {
        name: 'sftpHost',
        label: 'SFTP host',
        type: 'text',
        required: true,
        defaultValue: 'touringcarselectftp.blob.core.windows.net',
      },
      {name: 'sftpPort', label: 'SFTP port', type: 'number', required: true, defaultValue: 22},
      {name: 'sftpUser', label: 'SFTP user', type: 'text', required: true},
      {
        name: 'sftpPassword',
        label: 'SFTP password',
        type: 'password',
        required: true,
        secret: true,
      },
    ],
  },
  [AdvertisingPlatformCodeEnum.SPOTICAR]: {
    code: AdvertisingPlatformCodeEnum.SPOTICAR,
    fields: [
      {name: 'psaNetworkId', label: 'PSA network ID', type: 'text', required: true},
      {
        name: 'psaMainBrand',
        label: 'PSA main brand',
        type: 'choice',
        options: SPOTICAR_BRAND_OPTIONS,
        required: true,
      },
      {
        name: 'market',
        label: 'Market',
        type: 'choice',
        options: SPOTICAR_MARKET_OPTIONS,
        required: true,
      },
      {
        name: 'ftpHost',
        label: 'FTP host',
        type: 'text',
        required: true,
        defaultValue: 'ftp.clcg.fr',
      },
      {name: 'ftpPort', label: 'FTP port', type: 'number', required: true, defaultValue: 21},
      {name: 'ftpUser', label: 'FTP user', type: 'text', required: true},
      {name: 'ftpPassword', label: 'FTP password', type: 'password', required: true, secret: true},
      {name: 'ftpRootPath', label: 'FTP root path', type: 'text', defaultValue: ''},
      {name: 'passive', label: 'Passive FTP', type: 'checkbox', defaultValue: true},
      {name: 'timeoutMs', label: 'Timeout ms', type: 'number', defaultValue: 30000},
      {name: 'dsLuSpecialPath', label: 'DS LU special path', type: 'checkbox', defaultValue: true},
    ],
  },
  [AdvertisingPlatformCodeEnum.FASTBACK_B2B]: {
    code: AdvertisingPlatformCodeEnum.FASTBACK_B2B,
    fields: [
      {name: 'clientId', label: i18n.t('general.labels.clientId'), type: 'text', required: true},
      {
        name: 'clientSecret',
        label: i18n.t('general.labels.clientSecret'),
        type: 'password',
        required: true,
        secret: true,
      },
      {
        name: 'dealerId',
        label: i18n.t('page.settings.labels.dealerId'),
        type: 'choice',
        required: true,
        options: [],
        parse: (value) => (value === '' ? value : Number(value)),
        format: (value) => (value === undefined || value === null ? '' : String(value)),
      },
    ],
  },
};

// eslint-disable-next-line react/only-export-components -- Pre-existing violation
export function isPromptBridgeAdvertisingPlatform(
  code: AdvertisingPlatformCodeEnum | undefined
): code is keyof typeof promptBridgePlatformConfigs {
  return !!code && code in promptBridgePlatformConfigs;
}

// eslint-disable-next-line react/only-export-components -- Pre-existing violation
export function getPromptBridgePlatformConfig(code: AdvertisingPlatformCodeEnum) {
  return promptBridgePlatformConfigs[code];
}

// eslint-disable-next-line react/only-export-components -- Pre-existing violation
export function getAdvertisingBridgePlatformCode(code: AdvertisingPlatformCodeEnum): string {
  return code === AdvertisingPlatformCodeEnum.MOBILE_DE ? 'mobile-de' : code;
}

export function PlatformSettingsPromptBridge(props: {
  code: keyof typeof promptBridgePlatformConfigs;
}) {
  const branchId = useBranchId();
  const isFastback = props.code === AdvertisingPlatformCodeEnum.FASTBACK_B2B;
  const [fastbackDealers, setFastbackDealers] = useState<FastbackDealer[] | undefined>();
  const [fastbackCredentialsEdited, setFastbackCredentialsEdited] = useState(false);
  const [validateFastbackDealers, {isLoading: isValidatingFastbackDealers}] =
    useValidateFastbackDealersMutation();
  const config = getPromptBridgePlatformConfig(props.code);
  const advertisingBridgePlatformCode = getAdvertisingBridgePlatformCode(props.code);
  const {data, isFetching, isError} = useGetPromptBridgePlatformQuery({
    platformCode: advertisingBridgePlatformCode,
    branchId: isFastback ? undefined : branchId,
  });
  const [saveCredentials] = usePromptBridgePlatformAuthenticateMutation();
  const [deleteBranchCredentials] = useDeletePromptBridgePlatformBranchCredentialsMutation();

  const defaultValues = useMemo(
    () => (config ? createDefaultValues(config.fields, data?.credentials) : {}),
    [config, data?.credentials]
  );

  const schema = useMemo(() => createCredentialsFormSchema(config?.fields ?? []), [config]);
  const savedDealerId = isFastback ? Number(data?.credentials?.dealerId) : NaN;

  if (!config) {
    return null;
  }

  const handleSubmit = (
    values: PromptBridgeCredentialsFormData,
    _setErrors: unknown,
    reset: (values?: PromptBridgeCredentialsFormData, options?: {keepValues?: boolean}) => void
  ) =>
    saveCredentials({
      platformCode: advertisingBridgePlatformCode,
      branchId: isFastback ? undefined : branchId,
      body: toCredentialPayload(config.fields, values),
    })
      .unwrap()
      .then(() => {
        setFastbackCredentialsEdited(false);
        showNotification.success(
          i18n.t('entity.advertisingPlatforms.notifications.credentialsSaved')
        );
        reset(undefined, {keepValues: true});
      })
      .catch(handleApiError);

  const handleDeleteBranchOverride = () => {
    if (!branchId) {
      return;
    }

    openDeleteDialog({
      text: 'Delete branch credentials override?',
      onConfirm: () =>
        deleteBranchCredentials({platformCode: advertisingBridgePlatformCode, branchId})
          .unwrap()
          .then(() => showNotification.success(i18n.t('general.notifications.successfullyDeleted')))
          .catch(handleApiError),
    });
  };

  return (
    <DataStatus isLoading={isFetching} isError={isError}>
      <VStack spacing={4}>
        <Show when={!isFastback}>
          <CredentialsResolutionCard data={data} />
        </Show>
        <Card title={i18n.t('general.labels.authentication')}>
          <Text size="small" color="secondary">
            {i18n.t('page.settings.labels.authenticationDescription')}
          </Text>
          <Space vertical={4} />
          <Form<PromptBridgeCredentialsFormData>
            key={`${props.code}-${JSON.stringify(defaultValues)}`}
            defaultValues={defaultValues}
            experimentalZodSchema={schema}
            onSubmit={handleSubmit}
            shouldWatchForUnsavedChanges
          >
            {(control, formApi) => (
              <VStack spacing={4}>
                <Grid columns={2} spacing={4}>
                  {config.fields.map((field) => (
                    <FormField
                      key={field.name}
                      control={control}
                      name={field.name}
                      type={field.type}
                      label={field.label}
                      isRequired={field.required}
                      options={
                        field.name === 'dealerId'
                          ? (
                              fastbackDealers ??
                              (!fastbackCredentialsEdited &&
                              Number.isInteger(savedDealerId) &&
                              savedDealerId > 0
                                ? [
                                    {
                                      id: savedDealerId,
                                      name: `${i18n.t('page.settings.labels.dealerId')} ${savedDealerId}`,
                                    },
                                  ]
                                : [])
                            )?.map((dealer) => ({
                              label: dealer.name,
                              value: String(dealer.id),
                            }))
                          : field.options
                      }
                      helperText={field.helperText}
                      onChange={
                        isFastback && (field.name === 'clientId' || field.name === 'clientSecret')
                          ? () => {
                              setFastbackDealers(undefined);
                              setFastbackCredentialsEdited(true);
                              formApi.setValue('dealerId', '');
                            }
                          : undefined
                      }
                    />
                  ))}
                </Grid>
                <Right>
                  <HStack spacing={2}>
                    <Show when={isFastback && !fastbackDealers}>
                      <Button
                        variant="secondary"
                        title={i18n.t('general.actions.refresh')}
                        isLoading={isValidatingFastbackDealers}
                        onClick={() => {
                          const values = formApi.getValues();
                          const body = {
                            clientId: String(values.clientId ?? ''),
                            clientSecret: String(values.clientSecret ?? ''),
                          };
                          validateFastbackDealers({body})
                            .unwrap()
                            .then((result) => {
                              const current = formApi.getValues();
                              if (
                                current.clientId === body.clientId &&
                                current.clientSecret === body.clientSecret
                              ) {
                                setFastbackDealers(result.dealers);
                              }
                            })
                            .catch(handleApiError);
                        }}
                      />
                    </Show>
                    <Show when={!isFastback && branchId && data?.canDeleteBranchOverride}>
                      <Button
                        variant="secondary"
                        title="Delete branch override"
                        leftIcon="action/delete"
                        onClick={handleDeleteBranchOverride}
                      />
                    </Show>
                    <FormButton
                      control={control}
                      type="submit"
                      title={i18n.t('general.actions.save')}
                    />
                  </HStack>
                </Right>
              </VStack>
            )}
          </Form>
        </Card>
      </VStack>
    </DataStatus>
  );
}

function CredentialsResolutionCard(props: {data: PromptBridgePlatformResponseBody | undefined}) {
  const data = props.data;

  return (
    <Card title="Credentials scope">
      <VStack spacing={2}>
        <Text size="small">{credentialsStatusText(data)}</Text>
        <Text size="small" color="secondary">
          Branch credentials available: {yesNo(data?.branchCredentialsAvailable)}
        </Text>
        <Text size="small" color="secondary">
          Tenant credentials available: {yesNo(data?.tenantCredentialsAvailable)}
        </Text>
        <Text size="small" color="secondary">
          Tenant fallback enabled: {yesNo(data?.tenantFallbackEnabled)}
        </Text>
      </VStack>
    </Card>
  );
}

function credentialsStatusText(data: PromptBridgePlatformResponseBody | undefined): string {
  if (!data) {
    return '';
  }

  if (data.credentialsSource === 'branch') {
    return 'Using branch credentials override';
  }

  if (data.credentialsSource === 'tenant') {
    return 'Using tenant credentials';
  }

  if (data.tenantCredentialsAvailable && !data.tenantFallbackEnabled) {
    return 'Tenant credentials exist, but tenant fallback is disabled for this branch';
  }

  return 'No credentials configured';
}

function yesNo(value: boolean | undefined): string {
  return value ? i18n.t('general.labels.yes') : i18n.t('general.labels.no');
}

function createDefaultValues(
  fields: CredentialFieldConfig[],
  credentials: PromptBridgeCredentials | null | undefined
): PromptBridgeCredentialsFormData {
  return fields.reduce<PromptBridgeCredentialsFormData>((values, field) => {
    const value = credentials?.[field.name];

    if (field.secret) {
      values[field.name] = '';
      return values;
    }

    if (field.format) {
      values[field.name] = field.format(value ?? field.defaultValue);
      return values;
    }

    if (value !== undefined && value !== null) {
      values[field.name] = value;
      return values;
    }

    if (field.defaultValue !== undefined) {
      values[field.name] = field.defaultValue;
      return values;
    }

    values[field.name] = field.type === 'checkbox' ? false : '';
    return values;
  }, {});
}

function createCredentialsFormSchema(fields: CredentialFieldConfig[]) {
  return z.record(z.unknown()).superRefine((values, context) => {
    fields.forEach((field) => {
      if (!field.required) {
        return;
      }

      const value = values[field.name];
      const isMissing =
        value === undefined ||
        value === null ||
        value === '' ||
        (Array.isArray(value) && value.length === 0);

      if (isMissing) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: [field.name],
          message: i18n.t('general.errors.mixed.required'),
        });
      }
    });
  });
}

function toCredentialPayload(
  fields: CredentialFieldConfig[],
  values: PromptBridgeCredentialsFormData
): PromptBridgeCredentials {
  return fields.reduce<PromptBridgeCredentials>((payload, field) => {
    const parsedValue = field.parse ? field.parse(values[field.name]) : values[field.name];
    const value = field.type === 'number' && parsedValue !== '' ? Number(parsedValue) : parsedValue;

    if (value === '' || value === undefined || value === null) {
      return payload;
    }

    payload[field.name] = value;
    return payload;
  }, {});
}
